export class DownloadablePdf {


	doc = null

	pageWidth = 8.5

	pageHeight = 11

	pageLineHeight = 1.2

	pagePtsPerInch = 72

	contactUsTextOffset = null


	/**
	 * For code-splitting, we're using a dyanmic import to load jsPDF
	 *
	 * @return {<Promise>}
	 */
	createPdf() {
		return new Promise( resolve => {
			import( 'jspdf' ).then( jspdfLib => {
				const { jsPDF } = jspdfLib

				// eslint-disable-next-line new-cap
				this.doc = new jsPDF({
					unit: 'in',
					lineHeight: this.pageLineHeight,
					format: 'letter',
				}).setProperties({
					title: 'No Fault Surfaces - Color Mix',
				})

				resolve()
			})
		})
	}


	/**
	 * Add mix image
	 *
	 * @param {string} image Mix image
	 * @return {self}
	 */
	async addMixImage( image ) {
		const circularImage = await DownloadablePdf.constrainImageInCircle( image )

		this.doc.addImage(
			circularImage, // file
			'PNG', // format
			1, // x-coord
			1, // y-coord
			2.75, // image width
			2.75, // image height
		)

		return this
	}


	/**
	 * Create canvas with circle border
	 *
	 * @param {string} dataUrl
	 * @return {<Promise>} resolves to canvas data URL
	 */
	static constrainImageInCircle( imageUrl ) {
		return new Promise( resolve => {
			const image = new Image()

			image.onload = () => {
				const canvas = document.createElement( 'canvas' )
				const ctx = canvas.getContext( '2d' )

				ctx.canvas.width = 400
				ctx.canvas.height = 400

				ctx.lineWidth = 0
				ctx.strokeStyle = 'transparent'
				ctx.beginPath()

				ctx.arc( 200, 200, 200, 0, Math.PI * 2 )
				ctx.stroke()
				ctx.closePath()

				// prevents clipping from affecting other drawings
				ctx.save()
				ctx.clip()

				// adds black background underneath everything
				ctx.globalCompositeOperation = 'destination-under'
				ctx.fillStyle = '#000'
				ctx.fillRect( 0, 0, canvas.width, canvas.height )

				// adds actual pebble image
				ctx.drawImage( image, 0, 0, 400, 400 )

				ctx.restore()

				resolve( canvas.toDataURL() )
			}

			image.src = imageUrl
		})
	}


	/**
	 * Add logo image
	 *
	 * @param {string} logoUrl Logo image URL
	 * @param {bool} addLineBelow Add blue line beneath logo (and above breakout text)
	 * @return {self}
	 */
	addLogoImage( logoUrl, addLineBelow = true ) {
		this.doc.addImage(
			logoUrl, // file
			'PNG', // format
			5.5, // x-coord
			1.15, // y-coord
			1.35, // image width
			0.855, // image height
		)

		if ( addLineBelow ) {
			this.doc
				.setDrawColor( 0 )
				.setFillColor( 0, 147, 201 )
				.rect( 4.5, 2.5, 3, 0.01, 'F' )
		}

		return this
	}


	/**
	 * Add breakout text
	 *
	 * Text should be a single string, concatenated with "\n" for line breaks.
	 *
	 * @param {string} text Breakout text
	 * @return {self}
	 */
	addBreakoutText( text ) {
		this.doc
			.setFont( 'Times', 'bold' )
			.setFontSize( 16 )

		const lines = this.doc.splitTextToSize( text, 3 )

		this.doc.text( lines, 4.5, 3 )

		return this
	}


	/**
	 * Add disclaimer text
	 *
	 * Text should be a single string, concatenated with "\n" for line breaks.
	 *
	 * @param {string} text Disclaimer text
	 * @param {bool} addLineBelow Add blue line beneath disclaimer text
	 *
	 * @return {self}
	 */
	addDisclaimerText( text, addLineBelow = true ) {
		this.doc
			.setFont( 'Times', 'normal' )
			.setFontSize( 12 )

		const lines = this.doc.splitTextToSize( text, 6.5 )

		this.doc.text( lines, 1, 4.5 )

		// to help position the contact information *after* disclaimer text
		let offset = ( lines.length * 12 * this.pageLineHeight ) / this.pagePtsPerInch + 4.75

		if ( addLineBelow ) {
			this.doc
				.setDrawColor( 0 )
				.setFillColor( 0, 147, 201 )
				.rect( 1, offset, 6.5, 0.01, 'F' )

			offset += 0.25
		}

		this.contactUsTextOffset = offset

		return this
	}


	/**
	 * Add contact us text
	 *
	 * Text should be a single string, concatenated with "\n" for line breaks.
	 *
	 * @throws {Error} If contact us text offset is not set
	 *
	 * @param {string} text Contact us text
	 * @return {self}
	 */
	addContactUsText( text ) {
		if ( !this.contactUsTextOffset ) {
			throw new Error( 'Offset for contact us text not set. Call addDisclaimerText() first!' )
		}

		this.doc
			.setFont( 'Times', 'normal' )
			.setFontSize( 12 )

		// text *should* be short so no need to split lines

		this.doc.text( text, 1, this.contactUsTextOffset + 0.4 )

		return this
	}


	/**
	 * Save to user browser
	 *
	 * @param {string} fileName File name
	 * @return {self}
	 */
	saveToBrowser( fileName ) {
		this.doc.save( fileName )

		return this
	}


}
